import { ErrorBoundary as ReactErrorBoundary } from '@sentry/react';
import { HTMLAttributes, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useAuthData } from '~/auth';
import AppLayout from '~/components/layout/app/AppLayout';
import SideBar from '~/components/layout/SideBar';
import SmallTopBar from '~/components/layout/SmallTopBar';
import { formGapSizes } from '~/components/layout/templates/FormLayout';
import TopBar from '~/components/layout/TopBar';
import TopBarPublic from '~/components/layout/TopBarPublic';
import QuickNavProvider from '~/components/QuickNav/QuickNavProvider';
import Alert from '~/components/ui/Alert';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles.ts';

export function AutoDetectLayout({ children }: { children: React.ReactNode }) {
    const { authenticated } = useAuthData();
    if (authenticated) return <DefaultLayout>{children}</DefaultLayout>;
    return <PublicLayout>{children}</PublicLayout>;
}

export function DefaultLayout({ children }: { children?: React.ReactNode }) {
    const { user, refresh } = useAuthData();
    const [open, setOpen] = useState(false);
    return (
        <QuickNavProvider>
            <AppLayout data-test-component='layout/DefaultLayout'>
                <AppLayout.Sidebar>
                    <SideBar
                        user={user}
                        open={open}
                        onClose={() => setOpen(false)}
                    />
                </AppLayout.Sidebar>
                <AppLayout.TopBar>
                    <SmallTopBar onOpen={() => setOpen(true)} />
                    <TopBar
                        user={user}
                        refreshUser={refresh}
                    />
                </AppLayout.TopBar>
                <ErrorBoundary>
                    {children}
                    <Outlet />
                </ErrorBoundary>
            </AppLayout>
        </QuickNavProvider>
    );
}

type PublicLayoutProps = {
    children: React.ReactNode;
    hideButtons?: boolean;
} & DataTestAttributes;

export function PublicLayout({
    children,
    hideButtons,
    ['data-test-component']: testComponent = 'layout/PublicLayout',
}: PublicLayoutProps) {
    const { user } = useAuthData();
    const [open, setOpen] = useState(false);
    return (
        <AppLayout
            data-test-component={testComponent}
            className={classNames({
                'public-layout': true,
                'public-layout-authenticated': Boolean(user),
            })}>
            {user?.authenticated ? (
                <>
                    <AppLayout.Sidebar>
                        {open && (
                            <SideBar
                                mobileOnly={true}
                                user={user}
                                open={open}
                                onClose={() => setOpen(false)}
                            />
                        )}
                    </AppLayout.Sidebar>
                    <AppLayout.TopBar>
                        <SmallTopBar onOpen={() => setOpen(true)} />
                        <TopBar
                            user={user}
                            hideBreadcrumbs={true}
                            showLogo={true}
                        />
                    </AppLayout.TopBar>
                </>
            ) : (
                <AppLayout.TopBar>
                    <TopBarPublic hideButtons={hideButtons} />
                </AppLayout.TopBar>
            )}
            <ErrorBoundary>{children}</ErrorBoundary>
        </AppLayout>
    );
}

function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
    const { user } = useAuthData();
    const location = useLocation();
    const [initialPath] = useState(location.pathname);

    if (location.pathname !== initialPath) {
        resetError();
    }
    if (user.isRemotely()) {
        console.error(error);
    }
    return (
        <div className='error-boundary'>
            <Alert variant='danger'>
                An error occurred and this page could not be loaded.{' '}
                {user.isRemotely() ? `${error}.` : null}
            </Alert>
        </div>
    );
}

export function ErrorBoundary({ children }: { children: React.ReactNode }) {
    return <ReactErrorBoundary fallback={ErrorFallback}>{children}</ReactErrorBoundary>;
}

const FIXED_LAYOUT_SIZES = ['big', 'medium', 'small', 'xsmall'];

type FixedLayoutSize = (typeof FIXED_LAYOUT_SIZES)[number];

/**
 * @deprecated FixedLayout is deprecated and will be removed in a future release.
 * Please use PageLayout or other layout components instead.
 */
export function FixedLayout({
    size = 'big',
    className = '',
    children,
    ['data-test-component']: testComponent = 'layout/FixedLayout',
    ['data-test-screenshot-root']: testScreenshotRoot,
}: {
    size?: FixedLayoutSize;
    children: React.ReactNode;
    className?: string;
} & DataTestAttributes) {
    return (
        <div
            className={classNames({
                [`fixed-layout-${FIXED_LAYOUT_SIZES.includes(size) ? size : 'big'}`]: true,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-screenshot-root={testScreenshotRoot}>
            {children}
        </div>
    );
}

FixedLayout.Content = ({
    children,
    className = '',
    fullPageMobile = false,
    ...props
}: {
    children: React.ReactNode;
    className?: string;
    fullPageMobile?: boolean;
} & HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={classNames({
                'fixed-layout-content': true,
                [className]: Boolean(className),
                'full-page-mobile': fullPageMobile,
            })}
            {...props}>
            {children}
        </div>
    );
};

export function PageLayout({
    children,
    contained = false,
    className = '',
    ['data-test-component']: testComponent = 'layout/PageLayout',
}: {
    children: React.ReactNode;
    contained?: boolean;
    className?: string;
} & DataTestAttributes) {
    return (
        <div
            className={classNames({
                'layout-page': true,
                'layout-page-contained': contained,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-screenshot-root={true}>
            <div className='layout-page-content'>{children}</div>
        </div>
    );
}

export function scrollContentToTop() {
    document.querySelector('.layout-app-content')?.scrollTo({ top: 0, behavior: 'smooth' });
}

PageLayout.Group = ({
    children,
    gap = 'small',
    className = '',
}: {
    children: React.ReactNode;
    gap?: keyof typeof formGapSizes;
    className?: string;
}) => {
    return (
        <div
            className={classNames({
                [`layout-page-content-group gap-${formGapSizes[gap]}`]: true,
                [className]: Boolean(className),
            })}>
            {children}
        </div>
    );
};
